import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '../../context/I18nContext';
import { aiService } from '../../services/aiService';
import { storageService } from '../../services/storageService';
import { grcService } from '../../services/grcService';
import { AuditResult, AuditFinding, AuditComment, SoAControl } from '../../types/iso27001';
import { Framework, GRCControl, Supplier } from '../../types/grc';
import { Loader2, FileCheck, Globe, AlertTriangle, Upload, History, Eye, MessageSquare, Send, X, PlayCircle, CheckCircle2, Shield, FileText, Package, Users, Clock, Sparkles, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Modal } from '../ui/Modal';

interface AuditManagerProps {
  onNavigate?: (tab: string, filters?: any) => void;
  frameworkId?: number | null;
  framework?: Framework | null;
}

const SUPPLIERS_KEY = 'grc_suppliers';
const loadSuppliers = (): Supplier[] => {
  try { return JSON.parse(localStorage.getItem(SUPPLIERS_KEY) || '[]'); } catch { return []; }
};

export const AuditManager: React.FC<AuditManagerProps> = ({ onNavigate, frameworkId, framework }) => {
  const { t, language } = useI18n();
  const [loadingInternal, setLoadingInternal] = useState(false);
  const [loadingExternal, setLoadingExternal] = useState(false);
  const [errorInternal, setErrorInternal] = useState<string | null>(null);
  const [errorExternal, setErrorExternal] = useState<string | null>(null);
  const [auditHistory, setAuditHistory] = useState<AuditResult[]>([]);
  const [notifications, setNotifications] = useState<{id: string, message: string, type: 'warning' | 'info'}[]>([]);
  const [externalDocText, setExternalDocText] = useState('');
  
  const [selectedAudit, setSelectedAudit] = useState<AuditResult | null>(null);
  const [newComment, setNewComment] = useState('');

  // Deep Audit State
  const [isDeepAudit, setIsDeepAudit] = useState(false);
  const [auditProgress, setAuditProgress] = useState<string[]>([]);
  const [currentAuditStep, setCurrentAuditStep] = useState<string>('');
  const [auditPhase, setAuditPhase] = useState<string>('');
  const [expandHistory, setExpandHistory] = useState(false);

  // Background processing support
  const mountedRef = useRef(true);
  const abortRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setAuditHistory(storageService.getAuditResults());
    checkNotifications();
  }, []);

  const checkNotifications = () => {
    const notifs: {id: string, message: string, type: 'warning' | 'info'}[] = [];
    const today = new Date().toISOString().split('T')[0];
    
    const policies = storageService.getPolicies();
    policies.forEach(p => {
      if (p.nextReviewDate && p.nextReviewDate <= today) {
        notifs.push({ id: `p-${p.id}`, message: `Policy "${p.title}" is due for review (Date: ${p.nextReviewDate})`, type: 'warning' });
      }
    });

    const risks = storageService.getRisks();
    risks.forEach(r => {
      if (r.nextReviewDate && r.nextReviewDate <= today) {
        notifs.push({ id: `r-${r.id}`, message: `Risk "${r.description}" is due for review`, type: 'warning' });
      }
    });

    // Check supplier review dates
    const suppliers = loadSuppliers();
    suppliers.forEach(s => {
      if (s.next_review_date && s.next_review_date <= today) {
        notifs.push({ id: `s-${s.id}`, message: `Supplier "${s.name}" is due for review (Date: ${s.next_review_date})`, type: 'warning' });
      }
    });

    setNotifications(notifs);
  };

  const addProgressLog = useCallback((message: string, phase?: string) => {
    if (!mountedRef.current) return;
    const entry = `${new Date().toLocaleTimeString()} - ${message}`;
    setAuditProgress(prev => [...prev, entry]);
    setCurrentAuditStep(message);
    if (phase) setAuditPhase(phase);
  }, []);

  // Convert GRCControl to SoAControl for AI
  const toSoAControl = (c: GRCControl): SoAControl => ({
    id: c.control_code || `ctrl-${c.id}`,
    name: c.title,
    description: c.description || '',
    applicable: c.applicable === 1,
    justification: c.applicability_justification || '',
    implementationStatus: c.status === 'compliant' ? 'implemented' : c.status === 'partial' ? 'in_progress' : 'not_started',
    owner: c.owner || '',
    fulfillmentRationale: c.fulfillment_rationale || '',
    iso27002Guidance: c.implementation_notes || '',
    coveredByPolicies: c.suggested_policy_names || [],
    implementationSteps: c.implementation_steps || [],
  });

  const handleDeepAudit = async () => {
    setLoadingInternal(true);
    setErrorInternal(null);
    setIsDeepAudit(true);
    setAuditProgress([]);
    abortRef.current = false;
    
    try {
      const fwName = framework?.name || framework?.code || 'Selected Framework';
      addProgressLog(`Starting Deep Audit for ${fwName}...`, 'Initializing');
      
      // 1. Fetch Data from API & Local Storage
      addProgressLog('Fetching framework controls from API...', 'Data Collection');
      
      let fwControls: GRCControl[] = [];
      if (frameworkId) {
        fwControls = await grcService.getSoA(frameworkId);
      }
      
      const localRisks = storageService.getRisks();
      const localAssets = storageService.getAssets();
      const localPolicies = storageService.getPolicies();
      const localIncidents = storageService.getIncidents();
      const localSuppliers = loadSuppliers();
      
      if (!mountedRef.current) return;
      addProgressLog(`Loaded: ${fwControls.length} controls, ${localRisks.length} risks, ${localAssets.length} assets, ${localPolicies.length} policies, ${localIncidents.length} incidents, ${localSuppliers.length} suppliers`);

      // Build context summary (truncated for AI prompt size)
      const contextSummary = {
        framework: fwName,
        riskCount: localRisks.length,
        assetCount: localAssets.length,
        policyCount: localPolicies.length,
        incidentCount: localIncidents.length,
        supplierCount: localSuppliers.length,
        risksSummary: localRisks.slice(0, 20).map(r => ({ id: r.id, description: r.description, severity: r.severity, status: r.status, mitigations: r.mitigations })),
        assetsSummary: localAssets.slice(0, 20).map(a => ({ id: a.id, name: a.name, type: a.type, classification: a.classification })),
        policiesSummary: localPolicies.map(p => ({ id: p.id, title: p.title, status: p.status, coveredControls: p.coveredControls })),
      };

      // 2. Filter applicable controls
      const applicableControls = fwControls.filter(c => c.applicable === 1);
      const nonApplicable = fwControls.filter(c => c.applicable === 0);
      addProgressLog(`Found ${applicableControls.length} applicable controls, ${nonApplicable.length} marked N/A`, 'Control Analysis');

      const findings: AuditFinding[] = [];
      const today = new Date().toISOString().split('T')[0];

      // 3. Check each applicable control with AI
      if (applicableControls.length > 0) {
        addProgressLog(`Checking compliance for each applicable control...`, 'AI Compliance Check');
        
        for (let i = 0; i < applicableControls.length; i++) {
          if (abortRef.current || !mountedRef.current) { addProgressLog('Audit aborted.'); break; }

          const control = applicableControls[i];
          addProgressLog(`[${i + 1}/${applicableControls.length}] Checking ${control.control_code || control.title}...`);
          
          try {
            const soaCtrl = toSoAControl(control);
            const result = await aiService.checkControlCompliance(soaCtrl, contextSummary);
            
            if (!result.compliant) {
              findings.push({
                id: `F-${Date.now()}-${i}`,
                type: 'non_conformity',
                description: `Control ${control.control_code || control.title}: ${result.observation}`,
                relatedControlId: control.control_code || `ctrl-${control.id}`,
                relatedItemType: 'soa',
                relatedItemId: control.control_code || `ctrl-${control.id}`,
                status: 'open',
                dateIdentified: new Date().toISOString(),
                recommendation: result.recommendation || ''
              });
            }
          } catch (err) {
            console.error(`Failed to check control ${control.control_code}`, err);
            addProgressLog(`⚠ Error checking ${control.control_code || control.title}. Skipping.`);
          }
        }
      }

      if (!mountedRef.current) return;

      // 4. Evidence verification per control
      addProgressLog('Checking evidence adequacy per control...', 'Evidence Verification');
      for (const ctrl of applicableControls) {
        if (ctrl.evidence_count === 0) {
          findings.push({
            id: `F-EV-${Date.now()}-${ctrl.id}`,
            type: 'observation',
            description: `Control ${ctrl.control_code || ctrl.title} has no evidence attached.`,
            relatedControlId: ctrl.control_code || '',
            relatedItemType: 'soa',
            relatedItemId: ctrl.control_code || `ctrl-${ctrl.id}`,
            status: 'open',
            dateIdentified: new Date().toISOString(),
            recommendation: 'Attach supporting evidence (documents, screenshots, logs) to demonstrate implementation.'
          });
        }
      }

      // 5. Policy coverage check
      addProgressLog('Checking policy coverage across controls...', 'Policy Coverage');
      const policyCoveredControls = new Set<string>();
      localPolicies.forEach(p => p.coveredControls?.forEach(cc => policyCoveredControls.add(cc)));
      
      const uncoveredControls = applicableControls.filter(c => {
        const code = c.control_code || `ctrl-${c.id}`;
        return !policyCoveredControls.has(code) && (!c.linked_policy_ids || c.linked_policy_ids.length === 0);
      });
      
      if (uncoveredControls.length > 0) {
        findings.push({
          id: `F-POL-${Date.now()}`,
          type: 'observation',
          description: `${uncoveredControls.length} applicable controls lack policy coverage: ${uncoveredControls.slice(0, 5).map(c => c.control_code || c.title).join(', ')}${uncoveredControls.length > 5 ? '...' : ''}`,
          relatedControlId: '',
          relatedItemType: 'policy',
          relatedItemId: 'coverage-gap',
          status: 'open',
          dateIdentified: new Date().toISOString(),
          recommendation: 'Create or link policies to cover these controls. Use Policy Generator to create framework-specific policies.'
        });
      }

      // 6. Risk register adequacy
      addProgressLog('Checking risk register adequacy...', 'Risk Assessment');
      if (localRisks.length === 0) {
        findings.push({
          id: `F-RISK-${Date.now()}-0`,
          type: 'non_conformity',
          description: 'Risk register is empty. No risks have been identified or documented.',
          relatedControlId: '',
          relatedItemType: 'risk',
          relatedItemId: 'empty',
          status: 'open',
          dateIdentified: new Date().toISOString(),
          recommendation: 'Perform a risk assessment and document identified risks with severity, likelihood, and mitigation plans.'
        });
      } else {
        const highRisksWithoutMitigation = localRisks.filter(r => 
          (r.severity === 'critical' || r.severity === 'high') && (!r.mitigations || r.mitigations.length === 0)
        );
        if (highRisksWithoutMitigation.length > 0) {
          findings.push({
            id: `F-RISK-${Date.now()}-1`,
            type: 'non_conformity',
            description: `${highRisksWithoutMitigation.length} high/critical risks have no mitigation plan.`,
            relatedControlId: '',
            relatedItemType: 'risk',
            relatedItemId: 'mitigation-gap',
            status: 'open',
            dateIdentified: new Date().toISOString(),
            recommendation: 'Define mitigation strategies for all high and critical risks.'
          });
        }
        const overdueRisks = localRisks.filter(r => r.nextReviewDate && r.nextReviewDate < today);
        if (overdueRisks.length > 0) {
          findings.push({
            id: `F-RISK-${Date.now()}-2`,
            type: 'observation',
            description: `${overdueRisks.length} risks are overdue for review.`,
            relatedControlId: '',
            relatedItemType: 'risk',
            relatedItemId: 'overdue',
            status: 'open',
            dateIdentified: new Date().toISOString(),
            recommendation: 'Review and update all overdue risk assessments.'
          });
        }
      }

      // 7. Asset inventory adequacy
      addProgressLog('Checking asset inventory...', 'Asset Inventory');
      if (localAssets.length === 0) {
        findings.push({
          id: `F-ASSET-${Date.now()}`,
          type: 'non_conformity',
          description: 'Asset inventory is empty. No assets have been cataloged.',
          relatedControlId: '',
          relatedItemType: 'asset',
          relatedItemId: 'empty',
          status: 'open',
          dateIdentified: new Date().toISOString(),
          recommendation: 'Create a comprehensive asset inventory including hardware, software, data, and personnel assets.'
        });
      } else {
        const unclassified = localAssets.filter(a => !a.classification || a.classification === 'unclassified');
        if (unclassified.length > 0) {
          findings.push({
            id: `F-ASSET-CLASS-${Date.now()}`,
            type: 'observation',
            description: `${unclassified.length} assets are unclassified or missing classification.`,
            relatedControlId: '',
            relatedItemType: 'asset',
            relatedItemId: 'classification',
            status: 'open',
            dateIdentified: new Date().toISOString(),
            recommendation: 'Classify all assets according to their sensitivity and business impact.'
          });
        }
      }

      // 8. Incident management check
      addProgressLog('Checking incident records...', 'Incident Management');
      const openIncidents = localIncidents.filter(i => i.status === 'open' || i.status === 'investigating');
      if (openIncidents.length > 0) {
        findings.push({
          id: `F-INC-${Date.now()}`,
          type: 'observation',
          description: `${openIncidents.length} incidents are still open/investigating.`,
          relatedControlId: '',
          relatedItemType: 'incident',
          relatedItemId: 'open',
          status: 'open',
          dateIdentified: new Date().toISOString(),
          recommendation: 'Investigate and resolve all open incidents. Document root causes and corrective actions.'
        });
      }
      const incidentsWithoutRootCause = localIncidents.filter(i => (i.status === 'resolved' || i.status === 'closed') && !i.rootCause);
      if (incidentsWithoutRootCause.length > 0) {
        findings.push({
          id: `F-INC-RC-${Date.now()}`,
          type: 'observation',
          description: `${incidentsWithoutRootCause.length} resolved/closed incidents lack root cause analysis.`,
          relatedControlId: '',
          relatedItemType: 'incident',
          relatedItemId: 'root-cause',
          status: 'open',
          dateIdentified: new Date().toISOString(),
          recommendation: 'Conduct and document root cause analysis for all resolved incidents.'
        });
      }

      // 9. Supplier management check
      addProgressLog('Checking supplier management...', 'Supplier Management');
      if (localSuppliers.length > 0) {
        const nonCompliantSuppliers = localSuppliers.filter(s => s.compliance_status === 'non_compliant');
        if (nonCompliantSuppliers.length > 0) {
          findings.push({
            id: `F-SUP-NC-${Date.now()}`,
            type: 'non_conformity',
            description: `${nonCompliantSuppliers.length} suppliers are marked non-compliant: ${nonCompliantSuppliers.map(s => s.name).join(', ')}`,
            relatedControlId: '',
            relatedItemType: 'supplier',
            relatedItemId: 'non-compliant',
            status: 'open',
            dateIdentified: new Date().toISOString(),
            recommendation: 'Review contracts and compliance requirements for non-compliant suppliers. Consider remediation plans or alternative vendors.'
          });
        }
        const overdueSuppliers = localSuppliers.filter(s => s.next_review_date && s.next_review_date < today);
        if (overdueSuppliers.length > 0) {
          findings.push({
            id: `F-SUP-OD-${Date.now()}`,
            type: 'observation',
            description: `${overdueSuppliers.length} supplier reviews are overdue.`,
            relatedControlId: '',
            relatedItemType: 'supplier',
            relatedItemId: 'overdue',
            status: 'open',
            dateIdentified: new Date().toISOString(),
            recommendation: 'Schedule and complete supplier assessments for all overdue reviews.'
          });
        }
        const criticalWithoutAssessment = localSuppliers.filter(s => s.risk_tier === 'critical' && s.compliance_status === 'not_assessed');
        if (criticalWithoutAssessment.length > 0) {
          findings.push({
            id: `F-SUP-CRIT-${Date.now()}`,
            type: 'non_conformity',
            description: `${criticalWithoutAssessment.length} critical-tier suppliers have not been assessed.`,
            relatedControlId: '',
            relatedItemType: 'supplier',
            relatedItemId: 'critical-unassessed',
            status: 'open',
            dateIdentified: new Date().toISOString(),
            recommendation: 'Prioritize compliance assessment for all critical-tier suppliers.'
          });
        }
      }

      // 10. Documentation timeliness
      addProgressLog('Checking documentation timeliness...', 'Timeliness Check');
      const overduePolicies = localPolicies.filter(p => p.nextReviewDate && p.nextReviewDate < today);
      if (overduePolicies.length > 0) {
        findings.push({
          id: `F-DOC-${Date.now()}`,
          type: 'observation',
          description: `${overduePolicies.length} policies are overdue for review: ${overduePolicies.slice(0, 3).map(p => p.title).join(', ')}${overduePolicies.length > 3 ? '...' : ''}`,
          relatedControlId: '',
          relatedItemType: 'policy',
          relatedItemId: 'overdue',
          status: 'open',
          dateIdentified: new Date().toISOString(),
          recommendation: 'Review and update all overdue policies. Set realistic review cycles.'
        });
      }

      if (!mountedRef.current) return;

      // 11. General system audit (AI overall assessment)
      addProgressLog('Running AI overall system assessment...', 'Final Assessment');
      let generalSummary = '';
      try {
        const localSoA = applicableControls.map(c => toSoAControl(c));
        const generalAudit = await aiService.performInternalAudit({ 
          risks: localRisks.slice(0, 15), 
          assets: localAssets.slice(0, 15), 
          policies: localPolicies, 
          soa: localSoA.slice(0, 20),
          framework: fwName,
          incidentCount: localIncidents.length,
          supplierCount: localSuppliers.length,
          openIncidents: openIncidents.length,
        });
        
        // Add AI-generated findings  
        generalAudit.findings.forEach((f, idx) => {
          findings.push({
            ...f,
            id: `F-GEN-${Date.now()}-${idx}`,
            status: 'open' as const,
            dateIdentified: new Date().toISOString()
          });
        });
        generalSummary = generalAudit.summary;
      } catch (e) {
        addProgressLog('⚠ AI general assessment had an error. Continuing with manual findings.');
        generalSummary = 'AI general assessment could not complete.';
      }

      if (!mountedRef.current) return;

      addProgressLog(`Audit complete. Found ${findings.length} total issues.`, 'Complete');

      const result: AuditResult = {
        id: `AUD-${Date.now()}`,
        date: new Date().toISOString(),
        type: 'internal',
        findings: findings,
        summary: `Deep Audit for ${fwName}\n\nChecked ${applicableControls.length} applicable controls (${nonApplicable.length} N/A)\nRisks: ${localRisks.length} | Assets: ${localAssets.length} | Policies: ${localPolicies.length} | Incidents: ${localIncidents.length} | Suppliers: ${localSuppliers.length}\n\nFindings: ${findings.filter(f => f.type === 'non_conformity').length} non-conformities, ${findings.filter(f => f.type === 'observation').length} observations\n\n${generalSummary}`,
        comments: []
      };
      
      storageService.saveAuditResult(result);
      if (mountedRef.current) {
        setAuditHistory(storageService.getAuditResults());
        setSelectedAudit(result);
      }

    } catch (error: any) {
      console.error(error);
      if (mountedRef.current) {
        setErrorInternal(error.message || 'Deep Audit Failed');
        addProgressLog(`Audit Failed: ${error.message}`);
      }
    } finally {
      if (mountedRef.current) {
        setLoadingInternal(false);
        setIsDeepAudit(false);
      }
    }
  };

  const handleExternalAudit = async () => {
    if (!externalDocText) return;
    setLoadingExternal(true);
    setErrorExternal(null);
    try {
      const fwName = framework?.name || framework?.code || 'ISMS';
      const contextPrefix = `Framework: ${fwName}\n\n`;
      const resultData = await aiService.performExternalAudit(contextPrefix + externalDocText);

      const findings: AuditFinding[] = resultData.findings.map((f, index) => ({
        ...f,
        id: `F-${Date.now()}-${index}`,
        status: 'open' as const,
        dateIdentified: new Date().toISOString()
      }));

      const result: AuditResult = {
        id: `AUD-${Date.now()}`,
        date: new Date().toISOString(),
        type: 'external',
        findings: findings,
        summary: `External Audit Analysis for ${fwName}\n\n${resultData.summary}`,
        comments: []
      };

      storageService.saveAuditResult(result);
      if (mountedRef.current) {
        setAuditHistory(storageService.getAuditResults());
        setSelectedAudit(result);
      }
    } catch (error: any) {
      console.error(error);
      if (mountedRef.current) {
        setErrorExternal(error.message || 'External Audit Failed');
      }
    } finally {
      if (mountedRef.current) {
        setLoadingExternal(false);
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setExternalDocText(event.target?.result as string);
      };
      reader.readAsText(file);
    }
  };

  const handleAddComment = () => {
    if (!selectedAudit || !newComment.trim()) return;
    
    const comment: AuditComment = {
      id: `c-${Date.now()}`,
      user: 'Admin', // Mock user
      text: newComment,
      date: new Date().toISOString()
    };

    const updatedAudit = {
      ...selectedAudit,
      comments: [...(selectedAudit.comments || []), comment]
    };

    storageService.updateAuditResult(updatedAudit);
    setAuditHistory(storageService.getAuditResults());
    setSelectedAudit(updatedAudit);
    setNewComment('');
  };

  return (
    <div className="space-y-8">
      {/* Background processing indicator */}
      {isDeepAudit && (
        <div className="sticky top-0 z-40 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm font-medium">{t.audits.deepAuditProgress || 'Audit in progress'} — {auditPhase || (t.common.loading || 'Starting...')}</span>
          </div>
          <span className="text-xs text-indigo-200">
            {language === 'sr' 
              ? 'Možete napustiti stranicu. Revizija se nastavlja u pozadini.' 
              : 'You can navigate away. Audit continues in background.'}
          </span>
        </div>
      )}

      {notifications.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">{t.actions.required || 'Action Required'} ({t.audits.scheduledReviews || 'Scheduled Reviews'})</h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                <ul className="list-disc pl-5 space-y-1">
                  {notifications.map(n => (
                    <li key={n.id} className="flex items-center justify-between">
                      <span>{n.message}</span>
                      {onNavigate && (
                        <button 
                          onClick={() => {
                            if (n.id.startsWith('p-')) onNavigate('policy');
                            if (n.id.startsWith('r-')) onNavigate('risks');
                            if (n.id.startsWith('s-')) onNavigate('suppliers');
                          }}
                          className="text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-2 py-1 rounded border border-yellow-300 ml-2"
                        >
                          {t.actions.fix || 'Fix'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <FileCheck className="w-5 h-5 text-indigo-600" />
            {t.audits.internal || 'Internal Audit'} ({t.audits.aiPowered || 'AI Powered'})
          </h3>
          {framework && (
            <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-3 flex items-center gap-1">
              <Shield className="w-3 h-3" /> Framework: {framework.name || framework.code}
            </p>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Deep audit checks all applicable controls against {framework?.code || 'selected framework'}, verifies evidence, reviews risk/asset/incident/supplier records, and checks documentation timeliness.
          </p>
          
          {isDeepAudit ? (
            <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800 mb-4">
              <div className="flex items-center gap-2 mb-2 text-indigo-700 dark:text-indigo-300 font-medium">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="flex-1 truncate">{currentAuditStep}</span>
              </div>
              {auditPhase && (
                <div className="flex gap-1 mb-2 flex-wrap">
                  {['Data Collection', 'Control Analysis', 'AI Compliance Check', 'Evidence Verification', 'Policy Coverage', 'Risk Assessment', 'Asset Inventory', 'Incident Management', 'Supplier Management', 'Timeliness Check', 'Final Assessment', 'Complete'].map(phase => (
                    <span key={phase} className={`text-[10px] px-1.5 py-0.5 rounded ${
                      auditPhase === phase ? 'bg-indigo-200 dark:bg-indigo-700 text-indigo-800 dark:text-indigo-200 font-bold' : 
                      auditProgress.some(p => p.includes(phase)) ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 
                      'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    }`}>{phase}</span>
                  ))}
                </div>
              )}
              <div className="h-32 overflow-y-auto text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 font-mono">
                {auditProgress.map((log, i) => (
                  <div key={i}>{log}</div>
                ))}
              </div>
            </div>
          ) : (
            <button
              onClick={handleDeepAudit}
              disabled={loadingInternal || !frameworkId}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:bg-indigo-400 flex items-center gap-2 w-full justify-center"
            >
              {loadingInternal ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              {t.audits.startDeepAudit || 'Start Deep Audit'}
            </button>
          )}
          {!frameworkId && !isDeepAudit && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">Select a framework first to run the audit.</p>
          )}
          
          {errorInternal && <p className="text-xs text-red-600 mt-2">{errorInternal}</p>}
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Globe className="w-5 h-5 text-green-600" />
            {t.audits.external || 'External Audit'} / {t.actions.upload || 'Document Upload'}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Upload an existing audit report (TXT/MD) or paste text. AI will analyze findings and generate an improvement plan with specific actions.
          </p>
          
          <div className="mb-4">
             <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-6 h-6 mb-2 text-gray-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Upload TXT/MD/CSV file or paste audit results below</p>
                    </div>
                    <input type="file" className="hidden" onChange={handleFileUpload} accept=".txt,.md,.csv,.log,.json" />
                </label>
            </div>
           </div>

          <textarea
            className="w-full border dark:border-gray-600 rounded p-2 mb-4 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            rows={6}
            placeholder="Paste audit report text here... Include findings, observations, non-conformities, and any recommendations from the auditor."
            value={externalDocText}
            onChange={e => setExternalDocText(e.target.value)}
          />
          <button
            onClick={handleExternalAudit}
            disabled={loadingExternal || !externalDocText}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-green-400 flex items-center gap-2 w-full justify-center"
          >
            {loadingExternal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loadingExternal ? (t.common.loading || 'Analyzing...') : (t.audits.analyzeAndGenerate || 'Analyze & Generate Improvement Plan')}
          </button>
          {loadingExternal && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-2 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> AI is analyzing the document. You can navigate away.
            </p>
          )}
          {errorExternal && <p className="text-xs text-red-600 mt-2">{errorExternal}</p>}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <History className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            {t.audits.history || 'Audit History'} ({auditHistory.length})
          </h3>
          {auditHistory.length > 3 && (
            <button onClick={() => setExpandHistory(!expandHistory)} className="text-sm text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
              {expandHistory ? <><ChevronUp className="w-4 h-4" /> {t.actions.showLess || 'Show Less'}</> : <><ChevronDown className="w-4 h-4" /> {t.actions.showAll || 'Show All'}</>}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t.common.date || 'Date'}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t.audits.type || 'Type'}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t.audits.findings || 'Findings'}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t.common.actions || 'Actions'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {auditHistory.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">{t.audits.noAuditsYet || 'No audits performed yet.'}</td>
                </tr>
              ) : (
                (expandHistory ? auditHistory : auditHistory.slice(0, 3)).map(audit => (
                  <tr key={audit.id}>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{new Date(audit.date).toLocaleDateString()} {new Date(audit.date).toLocaleTimeString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 capitalize">{audit.type}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                          {audit.findings.filter(f => f.type === 'non_conformity').length} NC
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                          {audit.findings.filter(f => f.type === 'observation').length} Obs
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button onClick={() => setSelectedAudit(audit)} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center justify-end gap-1">
                        <Eye className="w-4 h-4" /> {t.actions.view || 'View'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedAudit && (
        <Modal isOpen={true} onClose={() => setSelectedAudit(null)} title={`${selectedAudit.type.toUpperCase()} Audit Report - ${new Date(selectedAudit.date).toLocaleDateString()}`}>
          <div className="space-y-6 max-h-[70vh] overflow-y-auto p-1">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{t.audits.executiveSummary || 'Executive Summary'}</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedAudit.summary}</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                {t.audits.findings || 'Findings'} & {t.audits.recommendations || 'Recommendations'}
              </h4>
              <div className="space-y-4">
                {selectedAudit.findings.map(finding => (
                  <div key={finding.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-sm transition-shadow bg-white dark:bg-gray-700">
                    <div className="flex justify-between items-start mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                        ${finding.type === 'non_conformity' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' : 
                          finding.type === 'observation' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'}`}>
                        {finding.type.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Related: {finding.relatedItemType} {finding.relatedItemId}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">{finding.description}</p>
                    <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded mt-2">
                      <p className="text-xs font-medium text-green-800 dark:text-green-300">{t.audits.recommendation || 'Recommendation'}:</p>
                      <p className="text-xs text-green-700 dark:text-green-400">{finding.recommendation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t dark:border-gray-600 pt-4">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                {t.audits.comments || 'Comments'} & {t.audits.notes || 'Notes'}
              </h4>
              <div className="space-y-3 mb-4">
                {selectedAudit.comments?.map(comment => (
                  <div key={comment.id} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm">
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{comment.user}</span>
                      <span>{new Date(comment.date).toLocaleString()}</span>
                    </div>
                    <p className="text-gray-800 dark:text-gray-200">{comment.text}</p>
                  </div>
                ))}
                {(!selectedAudit.comments || selectedAudit.comments.length === 0) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t.audits.noCommentsYet || 'No comments yet.'}</p>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder={t.audits.addComment || 'Add a comment...'}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <button onClick={handleAddComment} className="bg-indigo-600 text-white p-2 rounded hover:bg-indigo-700">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
